import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import UploadFile from 'components/Form/UploadFile';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { Read, Update } from 'services/financing-proposals';
import {
  Load,
  StatusContent,
  TableInfo,
  TableInfoPend,
  Title,
  Touch,
} from 'ui/styled';
import {
  exposeStrapiError,
  marcadorDiferente,
  parseStrapiImage,
  selectColors,
} from 'utils';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingDeskToPay() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);

  const init = useCallback(async () => {
    setLoading(true);
    const result = await Read('?filters[status]=a_pagar');
    setLoading(false);

    if (result && !exposeStrapiError(result)) {
      const nresult = result?.results?.sort((a, b) => b.id - a.id);
      const nextResult = nresult?.map((m) => ({
        ...m,
        bank: m?.propostas_banco?.find((f) => f.aceito),
      }));
      setRegisters(nextResult);
    }
  }, []);

  const save = useCallback(
    async (payload, item) => {
      if (loading) return;
      setLoading(true);
      //antes de enviar remove do payload o campo curadoria_analise
      delete payload.curadoria_analise;

      try {
        const result = await Update({ data: payload }, item?.id);
        if (result && !exposeStrapiError(result)) {
          toast.success('Data de pagamento salva com sucesso!');
          init();
        }
      } catch (error) {
        toast.error('Erro ao salvar a data de pagamento.');
      } finally {
        setLoading(false);
      }
    },
    [loading, init]
  );

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Verifica se foi analisado pela curadoria humana ou pelo bot
          const isAnalisadoCuradoria = row?.curadoria_analise === 'Analisado';
          const isAnalisadoBot = row?.curadoria_analise_bot === 'Analisado';
          const id = row?.id;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'Hora',
        ref: 'hora',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_TIME(row?.date),
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador ||
      (user?.distribuidores && user.distribuidores.length >= 1)
        ? [{ title: 'Distribuidora', sortable: true, ref: 'distributor' }]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      { title: 'Banco', sortable: true, ref: 'bank' },
      {
        title: 'nf de Produto',
        ref: 'sales_invoice',
        renderCell: ({ row }) =>
          loading === `${row?.id}-nf_venda` ? (
            <Load />
          ) : row?.original?.nf_venda?.url ? (
            <Touch
              onClick={() =>
                window.open(
                  parseStrapiImage(row?.original?.nf_venda?.url),
                  'new'
                )
              }
            >
              <TableInfo>{row?.sales_invoice}</TableInfo>
            </Touch>
          ) : (
            <TableInfo
              style={{ color: 'red' }}
              onClick={() =>
                toast.info('Não é permitido anexar este documento nesta fase')
              }
            >
              {row?.sales_invoice}
            </TableInfo>
          ),
      },
      {
        title: 'nf de serviço',
        ref: 'service_nf',
        renderCell: ({ row }) =>
          loading === `${row?.id}-nf_compra` ? (
            <Load />
          ) : row?.original?.nf_compra?.url ? (
            <Touch
              onClick={() =>
                window.open(
                  parseStrapiImage(row?.original?.nf_compra?.url),
                  'new'
                )
              }
            >
              <TableInfo>{row?.service_nf}</TableInfo>
            </Touch>
          ) : (
            <TableInfo
              style={{ color: 'red' }}
              onClick={() =>
                toast.info('Não é permitido anexar este documento nesta fase')
              }
            >
              {row?.service_nf}
            </TableInfo>
          ),
      },
      {
        title: 'CCB',
        ref: 'financing_contract',
        renderCell: ({ row }) =>
          loading === `${row?.id}-contrato_financiamento` ? (
            <Load />
          ) : row?.original?.contrato_financiamento?.url ? (
            <Touch
              onClick={() =>
                window.open(
                  parseStrapiImage(row?.original?.contrato_financiamento?.url),
                  'new'
                )
              }
            >
              <TableInfo>{row?.financing_contract}</TableInfo>
            </Touch>
          ) : (
            <TableInfo
              style={{ color: 'red' }}
              onClick={() =>
                toast.info('Não é permitido anexar este documento nesta fase')
              }
            >
              {row?.financing_contract}
            </TableInfo>
          ),
      },
      {
        title: 'Comprov. Pag',
        ref: 'comprovante',
        renderCell: ({ row }) =>
          loading === `${row?.id}-comprovante_pagamento` ? (
            <Load />
          ) : row?.original?.comprovante_pagamento?.url ? (
            <Touch
              onClick={() =>
                window.open(
                  parseStrapiImage(row?.original?.comprovante_pagamento?.url),
                  'new'
                )
              }
            >
              <TableInfo>{row?.comprovante}</TableInfo>
            </Touch>
          ) : user?.administrador || user?.perm_anexa_ccb ? (
            <UploadFile
              onChange={(file) =>
                save({ ...row?.original, comprovante_pagamento: file?.id }, row)
              }
              proposalType="A_Pagar"
            >
              <TableInfo style={{ color: 'red' }}>{row?.comprovante}</TableInfo>
            </UploadFile>
          ) : (
            <div
              onClick={(e) => {
                e.preventDefault();
                toast.error('Você não possui permissão para anexar.');
              }}
              style={{ cursor: 'pointer' }}
            >
              <TableInfoPend>{row?.comprovante}</TableInfoPend>
            </div>
          ),
      },
      {
        title: 'Ciência Compra',
        ref: 'ciencia',
        renderCell: ({ row }) =>
          loading === `${row?.id}-pdf_ciencia_compra` ? (
            <Load />
          ) : row?.original?.pdf_ciencia_compra?.url ? (
            <Touch
              onClick={() =>
                window.open(
                  parseStrapiImage(row?.original?.pdf_ciencia_compra?.url),
                  'new'
                )
              }
            >
              <TableInfo>{row?.ciencia}</TableInfo>
            </Touch>
          ) : null,
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => {
          return row?.comprovante === 'Anexado' ? (
            <Button
              color={selectColors('anexado')}
              small
              nospace
              mbutton
              onClick={() =>
                setModal({
                  type: 'confirm_date',
                  title: 'Data de Pagamento',
                  placeholder: 'Informe a data de pagamento:',
                  loading,
                  onConfirm: async (date) => {
                    await save(
                      {
                        data_pago: date,
                        status: 'concluido',
                      },
                      row?.original
                    );

                    setModal(null);
                  },
                })
              }
            >
              Informar Data de Pagamento
            </Button>
          ) : (
            <StatusContent
              color={selectColors(
                row?.comprovante === 'Anexar' ? 'a pagar' : ''
              )}
            >
              {row?.comprovante === 'Anexar'
                ? 'Aguardando pagamento do Banco'
                : 'Docs. em análise'}
            </StatusContent>
          );
        },
      },
      {
        title: 'Espelho da Proposta',
        ref: 'espelho',
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => navigate(`proposals/financing/${row?.id}`)}
              color="blue"
              nospace
              mbutton
              small
            >
              Visualizar
            </Button>
          </div>
        ),
      },
      ...(user?.administrador
        ? [
            {
              title: 'Observações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => {
                    setModal({
                      type: 'observacao',
                      dados: row,
                      origem: 'A Pagar',
                      reload: () => init(),
                    });
                  }}
                  color="botao"
                  nospace
                  mbutton
                  small
                >
                  Adicionar
                </Button>
              ),
            },
          ]
        : []),
    ];
    return defaultColumns;
  }, [loading, user, navigate, setModal, init, save]);

  const rows = useMemo(() => {
    return registers?.map((item) => {
      const propostaAprovada = item?.propostas_banco?.find(
        (p) => p.status === 'aprovado'
      );
      return {
        original: item,
        id: item?.id,
        date: item?.createdAt,
        formatedDate: formatadores.TIMESTAMP_TO_BR(item?.createdAt),
        cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
        distributor: normalizeName(item?.distribuidor?.trade_name),
        integrator: normalizeName(item?.integrador?.nome_fantasia),
        seller: normalizeName(item?.informacoes_projeto?.nome_vendedor),
        telephone: item?.informacoes_cliente?.telefone_celular,
        customer: normalizeName(item?.informacoes_cliente?.nome_completo),
        value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
        status: normalizeName(item?.status),
        bank: normalizeName(propostaAprovada?.banco?.nome_fantasia),
        sales_invoice: item?.nf_venda?.url ? 'Anexado' : 'Anexar',
        service_nf: item?.nf_compra?.url ? 'Anexado' : 'Anexar',
        financing_contract: item?.contrato_financiamento?.url
          ? 'Anexado'
          : 'Anexar',
        ciencia: item?.pdf_ciencia_compra?.url ? 'Gerado' : 'Não gerado',
        comprovante: item?.comprovante_pagamento?.url ? 'Anexado' : 'Anexar',
        curadoria_analise: item?.curadoria_analise,
        curadoria_analise_bot: item?.curadoria_analise_bot,
        cnpj_integrador: item?.integrador?.cnpj,
        cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
        cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
        cpf_cnpj_clean: item?.cliente?.cpf
          ? item?.cliente?.cpf.replace(/\D/g, '')
          : item?.cliente?.cnpj.replace(/\D/g, ''),
      };
    });
  }, [registers]);

  useEffect(() => init(), [init]);

  return (
    <ContainerAuthenticated>
      <Title>Mesa de Financiamentos</Title>
      <TableWrapper title={'a Pagar'} search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
